import React, { ReactNode, Suspense, useEffect } from 'react'

// redux
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../redux/store'
import * as layoutConstants from '../constants/layout'
import { ViewUserDetailsResponse } from '@/helpers/api/WebApiClient'

// hooks
import { useViewPort } from '../hooks'
import { changeHTMLAttribute } from '../utils'
import { changeSideBarType, hydrateAuthSession, changeSideBarTheme } from '../redux/actions'
import { APICore } from '../helpers/api/apiCore'
import { GlobalPreloader, Preloader } from '../components'
import { isDashboardShellRoute } from '@/constants/dashboardShell'
import { useLocation } from 'react-router-dom'

// code splitting and lazy loading
// https://blog.logrocket.com/lazy-loading-components-in-react-16-6-6cea535c0b52
const Topbar = React.lazy(() => import('./Topbar'))
const LeftSideBar = React.lazy(() => import('./LeftSideBar'))
const Footer = React.lazy(() => import('./Footer'))
const RightSideBar = React.lazy(() => import('./RightSideBar'))

const loading = () => <div />

interface VerticalLayoutProps {
	children?: ReactNode
}

const VerticalLayout = ({ children }: VerticalLayoutProps) => {
	const dispatch = useDispatch<AppDispatch>()
	const { width } = useViewPort()
	const { pathname } = useLocation()
	const onDashboardShell = isDashboardShellRoute(pathname)
	const userData = useSelector((state: RootState) => state.Auth.userData) as ViewUserDetailsResponse | Record<string, never>

	const layoutTheme = useSelector((state: RootState) => state.Layout.layoutTheme)
	const layoutDirection = useSelector((state: RootState) => state.Layout.layoutDirection)
	const layoutWidth = useSelector((state: RootState) => state.Layout.layoutWidth)
	const topBarTheme = useSelector((state: RootState) => state.Layout.topBarTheme)
	const sideBarTheme = useSelector((state: RootState) => state.Layout.sideBarTheme)
	const sideBarType = useSelector((state: RootState) => state.Layout.sideBarType)
	const layoutPosition = useSelector((state: RootState) => state.Layout.layoutPosition)

	/**
	 * Layout defaults
	 */

	useEffect(() => {
		if (
			layoutTheme === layoutConstants.LayoutTheme.THEME_LIGHT &&
			sideBarTheme === layoutConstants.SideBarTheme.LEFT_SIDEBAR_THEME_DARK
		) {
			dispatch(changeSideBarTheme(layoutConstants.SideBarTheme.LEFT_SIDEBAR_THEME_LIGHT) as any)
		}
		// One-time alignment for sessions that toggled layout before sidebar sync existed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		changeHTMLAttribute('data-mode', layoutTheme)
	}, [layoutTheme])

	useEffect(() => {
		changeHTMLAttribute('dir', layoutDirection)
	}, [layoutDirection])

	useEffect(() => {
		changeHTMLAttribute('data-layout-width', layoutWidth)
	}, [layoutWidth])

	useEffect(() => {
		changeHTMLAttribute('data-topbar-color', topBarTheme)
	}, [topBarTheme])

	useEffect(() => {
		changeHTMLAttribute('data-menu-color', sideBarTheme)
	}, [sideBarTheme])

	useEffect(() => {
		changeHTMLAttribute('data-sidenav-view', sideBarType)
	}, [sideBarType])

	useEffect(() => {
		changeHTMLAttribute('data-layout-position', layoutPosition)
	}, [layoutPosition])

	useEffect(() => {
		const api = new APICore()
		if (!api.isUserAuthenticated()) return

		const hasProfile = userData && 'email' in userData && Boolean(userData.email)
		if (!hasProfile) {
			dispatch(hydrateAuthSession() as any)
		}
	}, [dispatch, userData])

	useEffect(() => {
		document.getElementsByTagName('html')[0].removeAttribute('data-layout')
	}, [])

	useEffect(() => {
		if (width < 768) {
			dispatch(changeSideBarType(layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_MOBILE)as any)
		} else if (width < 1140) {
			dispatch(changeSideBarType(layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_SMALL)as any)
		} else if (width >= 1140) {
			dispatch(changeSideBarType(layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_DEFAULT)as any)
		}
	}, [width, dispatch])

	const isCondensed = sideBarType === layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_SMALL
	const isLight = sideBarTheme === layoutConstants.SideBarTheme.LEFT_SIDEBAR_THEME_LIGHT

	return (
		<>
			<Suspense fallback={loading()}>
				<div className={`flex min-h-screen w-full min-w-0 wrapper ${onDashboardShell ? 'dashboard-shell' : 'bg-background'}`}>
					<Suspense fallback={loading()}>
						<LeftSideBar isCondensed={isCondensed} isLight={isLight} hideUserProfile={true} />
					</Suspense>

					<div className="page-content min-w-0 flex-1">
						<Suspense fallback={loading()}>
							<div className="sticky top-0 z-30 flex shrink-0 flex-col">
								<Topbar />
								<GlobalPreloader />
							</div>
						</Suspense>

						<main className="flex-1 p-6 lg:p-8">
							<Suspense fallback={<Preloader />}>{children}</Suspense>
						</main>

						<Footer />
					</div>
				</div>

				<Suspense fallback={loading()}>
					<RightSideBar />
				</Suspense>
			</Suspense>
		</>
	)
}

export default VerticalLayout
